import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Camera, Upload, FileText, Download, Share2, Trash2, Loader2,
  RotateCcw, ZoomIn, ZoomOut, Maximize, ScanLine, Save, Mail
} from 'lucide-react';
import { getErrorMessage } from '../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocumentScanner = ({ onSave, onShare, communityId }) => {
  const [image, setImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileName, setFileName] = useState('');
  const [format, setFormat] = useState('png');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  
  // Settings
  const [enhanceContrast, setEnhanceContrast] = useState(true);
  const [grayscale, setGrayscale] = useState(false);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImage(event.target.result);
        setProcessedImage(null);
        setFileName(file.name.replace(/\.[^/.]+$/, ''));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleScan = async () => {
    if (!image) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/docs/scan`, {
        image_data: image,
        enhance_contrast: enhanceContrast,
        grayscale: grayscale
      });
      
      setProcessedImage(response.data.processed_image);
      toast.success('Document scanned successfully!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Scan failed'));
    } finally {
      setProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!processedImage) return;
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/docs/scan/save`, {
        image_data: processedImage,
        file_name: fileName || 'scan',
        format: format,
        community_id: communityId
      });
      
      toast.success('Document saved!');
      setSaveDialogOpen(false);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = response.data.file_data;
      link.download = response.data.file_name;
      link.click();
      
      if (onSave) onSave(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async (method) => {
    if (!processedImage) return;
    
    try {
      // First save the document
      const saveResponse = await axios.post(`${API}/docs/scan/save`, {
        image_data: processedImage,
        file_name: fileName || 'scan',
        format: format
      });
      
      if (method === 'community' && communityId) {
        const response = await axios.post(`${API}/docs/scan/share`, {
          document_id: saveResponse.data.document_id,
          community_id: communityId
        });
        toast.success('Shared to community!');
        if (onShare) onShare(response.data);
      } else if (method === 'email' && shareEmail) {
        // Open email client with attachment info
        const mailtoLink = `mailto:${shareEmail}?subject=Scanned Document&body=Please find the attached scanned document.`;
        window.open(mailtoLink);
        toast.success('Email client opened');
      }
      
      setShareDialogOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Share failed'));
    }
  };

  const reset = () => {
    setImage(null);
    setProcessedImage(null);
    setFileName('');
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      {!image && (
        <Card className="bg-slate-900/50 border-slate-700 border-dashed">
          <CardContent className="p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <ScanLine className="w-16 h-16 text-amber-500" />
              <h3 className="text-xl font-semibold">Scan Document</h3>
              <p className="text-slate-400 mb-4">Take a photo or upload an image to convert to a scan</p>
              
              <div className="flex gap-4">
                <Button 
                  onClick={() => cameraInputRef.current?.click()}
                  className="gap-2"
                >
                  <Camera className="w-4 h-4" />
                  Take Photo
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Upload Image
                </Button>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Section */}
      {image && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {processedImage ? 'Scanned Document' : 'Original Image'}
            </h3>
            <Button variant="ghost" size="sm" onClick={reset}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
          </div>

          {/* Image Preview */}
          <Card className="bg-slate-900 border-slate-700 overflow-hidden">
            <CardContent className="p-4">
              <img 
                src={processedImage || image} 
                alt="Document preview"
                className="max-w-full max-h-[500px] mx-auto rounded-lg shadow-lg"
              />
            </CardContent>
          </Card>

          {/* Settings */}
          {!processedImage && (
            <Card className="bg-slate-900/50 border-slate-700">
              <CardContent className="p-4">
                <h4 className="font-medium mb-3">Scan Settings</h4>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enhanceContrast}
                      onChange={(e) => setEnhanceContrast(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    Enhance Contrast
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={grayscale}
                      onChange={(e) => setGrayscale(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    Black & White
                  </label>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            {!processedImage ? (
              <Button 
                onClick={handleScan}
                disabled={processing}
                className="bg-amber-500 hover:bg-amber-600 text-black gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ScanLine className="w-4 h-4" />
                    Scan Document
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button 
                  onClick={() => setSaveDialogOpen(true)}
                  className="bg-green-600 hover:bg-green-700 gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShareDialogOpen(true)}
                  className="gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => setProcessedImage(null)}
                  className="gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Re-scan
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5 text-green-500" />
              Save Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>File Name</Label>
              <Input
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Enter file name..."
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Format</Label>
              <div className="flex gap-2">
                {['png', 'jpeg', 'pdf'].map((f) => (
                  <Badge
                    key={f}
                    variant={format === f ? 'default' : 'outline'}
                    className={`cursor-pointer ${format === f ? 'bg-amber-500' : ''}`}
                    onClick={() => setFormat(f)}
                  >
                    {f.toUpperCase()}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save & Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-500" />
              Share Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {communityId && (
              <Button 
                onClick={() => handleShare('community')}
                className="w-full justify-start gap-2"
                variant="outline"
              >
                <FileText className="w-4 h-4" />
                Share to Community
              </Button>
            )}
            <div className="space-y-2">
              <Label>Share via Email</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="bg-slate-800 border-slate-700"
                />
                <Button onClick={() => handleShare('email')} disabled={!shareEmail}>
                  <Mail className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentScanner;
